import React, { useState, useMemo } from "react";
import { Inbox, Search, X } from "lucide-react";
import BenefitGrid from "./BenefitGrid";
import BenefitFullfilment from "./BenefitFullfilment";
import BenefitSummaryPanel from "./BenefitSummaryPanel";
import useBenefits from "../hooks/useBenefits";
import { getBenefitId } from "../utils/BenefitUtils";

export const BENEFIT_TENANT_ID = "T1111001XT7";

const CustomizePlanSection = ({ onBuy, isActive = true }) => {
  const { benefits, loading, error, refetch } = useBenefits(BENEFIT_TENANT_ID);

  const [detailsBenefit, setDetailsBenefit] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [search, setSearch] = useState("");

  const safeBenefits = useMemo(
    () => (Array.isArray(benefits) ? benefits : []),
    [benefits]
  );

  const visibleBenefits = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return safeBenefits;
    return safeBenefits.filter((b) =>
      (b?.name || "").toLowerCase().includes(query)
    );
  }, [safeBenefits, search]);

  const handleViewDetails = (benefit) => {
    if (!benefit) return;
    setDetailsBenefit(benefit);
    setDetailsOpen(true);
  };

  const handleToggleSelect = (benefit) => {
    const id = getBenefitId(benefit, safeBenefits.indexOf(benefit));
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const selectedBenefits = safeBenefits.filter((b, i) =>
    selectedIds.has(getBenefitId(b, i))
  );

  const handleRemoveSelected = (benefit) => {
    const id = getBenefitId(benefit, safeBenefits.indexOf(benefit));
    const next = new Set(selectedIds);
    next.delete(id);
    setSelectedIds(next);
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBuySelection = (chosenBenefits) => {
    if (!chosenBenefits || chosenBenefits.length === 0) return;
    setSummaryOpen(false);
    onBuy({
      tenantId: BENEFIT_TENANT_ID,
      planCode: "",
      members: 0,
      isRider: false,
      showMember: false,
      benefits: chosenBenefits,
      onPurchaseComplete: clearSelection,
    });
  };

  return (
    <section aria-labelledby="customize-plan-heading">
      
      <div
        className="
    relative isolate overflow-hidden
    rounded-2xl
    border border-purple-100
    bg-gradient-to-br from-[#FAF8FF] via-[#F8F5FF] to-[#F3EEFF]
    px-5 py-6
  "
      >
        {/* Decorative background elements */}
        <div
          className="
      pointer-events-none absolute -right-20 -top-24
      h-64 w-64 rounded-full
      bg-purple-200/30 blur-3xl
    "
        />

        <div
          className="
      pointer-events-none absolute -bottom-24 left-1/3
      h-48 w-48 rounded-full
      bg-indigo-200/20 blur-3xl
    "
        />

        <div className="relative flex flex-col gap-5 md:gap-0 md:flex-row md:items-center md:justify-between">
          {/* Content */}
          <div className="min-w-0 flex-1">
            {/* Eyebrow */}
            <div
              className="
          mb-3 inline-flex items-center gap-2
          rounded-full
          border border-purple-200
          bg-white/80
          px-3 py-1.5
          shadow-sm
        "
            >
              <span className="h-1.5 w-1.5 rounded-full bg-purple-600" />

              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-purple-700 sm:text-xs">
                Personalized Healthcare
              </span>
            </div>

            {/* Heading */}
            <h2
              id="customize-plan-heading"
              className="
          max-w-[620px]
          text-[28px]
          font-extrabold
          leading-[1.08]
          tracking-[-0.025em]
          text-[#14131A]
          sm:text-[32px]
          md:text-[38px]
          lg:text-[40px]
        "
            >
              Create a healthcare plan{" "}
              <span
                className="
            bg-gradient-to-r
            from-[#6D28D9]
            via-[#7C3AED]
            to-[#9333EA]
            bg-clip-text
            text-transparent
          "
              >
                designed{" "}
              </span>
               for you 
            </h2>
           
            {/* Description */}
            <p
              className="
          mt-3
          max-w-[540px]
          text-sm
          leading-6
          text-[#5B5866]
          sm:text-base
        "
            >
              Select the healthcare benefits that matter most to you and build
              a plan that truley fits your lifestyle and health goals.
            </p>

            <div className="mt-5 flex flex-nowrap gap-2">
        <div
          className="
            inline-flex items-center gap-2
            rounded-full
            bg-white/90
            px-2 py-2
            text-xs font-medium
            text-purple-800
            shadow-sm
            ring-1 ring-purple-200
          "
        >
          <span className="text-purple-800">✓</span>
          Personalised for you
        </div>
        <div
          className="
            inline-flex items-center gap-2
            rounded-full
            bg-white/90
            px-2 py-2
            text-xs font-medium
            text-purple-800
            shadow-sm
            ring-1 ring-purple-200
          "
        >
          <span className="text-purple-800">✓</span>
          Trusted Benefits 
        </div>
        <div
          className="
            inline-flex items-center gap-2
            rounded-full
            bg-white/90
            px-2 py-2
            text-xs font-medium
            text-purple-800
            shadow-sm
            ring-1 ring-purple-200
          "
        >
          <span className="text-purple-800">✓</span>
          Flexible Choices
        </div>

        
      </div>
          </div>

          
          <img
                src="/assets/images/banner-1.png"
                alt="Personalized healthcare plan"
                className="
            relative
            h-auto
            w-[440px]
            object-cover
            
            md:w-[390px]
            lg:w-[630px] 
          "
              />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {!loading && !error && safeBenefits.length > 0 && (
          <div className="relative max-w-sm flex-1">
            {/* <Search
              size={16}
              strokeWidth={2}
              className="pointer-events-none absolute right-7 top-1/2 -translate-y-1/2 text-[#8A8798]"
            /> */}
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search benefits…"
              aria-label="Search benefits"
              className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-[#E8E5F0] bg-white text-sm text-[#14131A] placeholder:text-[#8A8798] outline-none focus:border-[#6D28D9] transition-colors"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8798] hover:text-[#14131A]"
              >
                <X size={15} strokeWidth={2.25} />
              </button>
            )}
          </div>
        )}
      </div>

      <div className="mt-5 pb-6" aria-live="polite">
        {loading && <BenefitGrid loading skeletonCount={8} />}

        {!loading && error && (
          <div
            role="alert"
            className="flex flex-col items-center justify-center py-16 gap-3 text-center"
          >
            <p className="text-base font-semibold text-red-600">
              Couldn't load benefits right now.
            </p>
            <p className="text-sm text-[#8A8798] max-w-sm">{error}</p>
            <button
              type="button"
              onClick={refetch}
              className="mt-1 px-5 py-2 rounded-xl bg-[#14131A] text-white text-sm font-semibold hover:bg-black transition-colors"
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && safeBenefits.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-2 text-center">
            <div className="h-12 w-12 rounded-full bg-[#F7F5FB] flex items-center justify-center mb-1">
              <Inbox size={20} className="text-[#C9BEE8]" strokeWidth={1.75} />
            </div>
            <p className="text-base font-semibold text-[#14131A]">
              No benefits available yet
            </p>
            <p className="text-sm text-[#8A8798]">
              Check back soon, or contact us to set one up.
            </p>
          </div>
        )}

        {!loading &&
          !error &&
          safeBenefits.length > 0 &&
          visibleBenefits.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-center">
              <p className="text-base font-semibold text-[#14131A]">
                No benefits match “{search}”
              </p>
              <button
                type="button"
                onClick={() => setSearch("")}
                className="mt-1 text-sm font-semibold text-[#6D28D9] hover:underline"
              >
                Clear search
              </button>
            </div>
          )}

        {!loading && !error && visibleBenefits.length > 0 && (
          <BenefitGrid
            benefits={visibleBenefits}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onViewDetails={handleViewDetails}
          />
        )}

      </div>

      {/* Selection bar — pinned to the bottom of the viewport while browsing benefits.
          Mobile gets a compact single row (count · amount · CTA); desktop adds
          the removable chip list since there's room for it. This section is
          only ever mounted while its tab is active, so `isActive` here is
          just a safety guard, not the primary visibility switch. */}
      {selectedIds.size > 0 && isActive && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E8E5F0] shadow-[0_-4px_20px_rgba(20,19,26,0.06)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            {/* Desktop: label + chips */}
            <div className="hidden sm:block mb-1">
              <p className="text-sm font-semibold text-[#14131A]">
                Your selected benefits
                <span className="ml-1.5 font-normal text-[#8A8798]">
                  · {selectedIds.size} selected
                </span>
              </p>
              <div className="mt-1.5 flex gap-2 overflow-x-auto no-scrollbar pb-1">
                {selectedBenefits.map((b, i) => (
                  <span
                    key={getBenefitId(b, i)}
                    className="flex items-center gap-1.5 shrink-0 whitespace-nowrap px-3 py-1.5 rounded-lg bg-[#F7F5FB] text-[#14131A] text-xs font-medium"
                  >
                    {b?.name || "Untitled Benefit"}
                    <button
                      type="button"
                      onClick={() => handleRemoveSelected(b)}
                      className="text-[#8A8798] hover:text-[#14131A]"
                      aria-label={`Remove ${b?.name}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between gap-4">
              {/* Mobile: compact count + amount */}
              <div className="sm:hidden min-w-0">
                <p className="text-sm font-bold text-[#14131A]">
                  {selectedIds.size} benefit{selectedIds.size === 1 ? "" : "s"}{" "}
                  selected
                </p>
                <p className="text-xs font-semibold text-[#6D28D9]">
                  ₹
                  {selectedBenefits
                    .reduce((sum, b) => sum + (b?.benefitAmount || 0), 0)
                    .toLocaleString("en-IN")}
                </p>
              </div>

              {/* Desktop: amount */}
              <div className="hidden sm:block text-right">
                <p className="text-xs font-medium text-[#8A8798]">
                  Total amount
                </p>
                <p className="text-base font-bold text-[#14131A]">
                  ₹
                  {selectedBenefits
                    .reduce((sum, b) => sum + (b?.benefitAmount || 0), 0)
                    .toLocaleString("en-IN")}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSummaryOpen(true)}
                className="shrink-0 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#4C1D95] text-white text-sm font-semibold hover:bg-[#3B0764] active:scale-[0.99] transition-all duration-150 whitespace-nowrap"
              >
                Review Plan
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <BenefitFullfilment
        benefit={detailsBenefit}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
      />

      <BenefitSummaryPanel
        open={summaryOpen}
        selectedBenefits={selectedBenefits}
        onClose={() => setSummaryOpen(false)}
        onViewDetails={(benefit) => {
          setSummaryOpen(false);
          handleViewDetails(benefit);
        }}
        onBuyPlan={handleBuySelection}
        buying={false}
      />
    </section>
  );
};

export default CustomizePlanSection;
